import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { backgroundQuestions } from '../data/backgroundQuestions';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

interface ProfileUpdateProps {
  onClose: () => void;
  onSave: () => void;
  enrolledClasses?: any[];
}

interface FormData {
  [key: string]: any;
}

interface ConditionalField {
  field: string;
  value: string;
}

interface Question {
  id: string;
  text: string;
  type: string;
  options?: string[] | { value: number; label: string }[];
  conditional?: ConditionalField;
  subQuestions?: Question[];
}


const ProfileUpdate = ({ onClose, onSave, enrolledClasses = [] }: ProfileUpdateProps) => {
  const { user } = useAuth();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [formData, setFormData] = useState<FormData>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentSection = backgroundQuestions[currentSectionIndex];

  useEffect(() => {
    // Load existing profile data and pre-populate with user info
    const loadExistingProfile = async () => {
      if (!user) return;
      
      // Pre-populate with user information
      const initialData: FormData = {
        name: user.user_metadata?.full_name || '',
        email: user.email || ''
      };
      
      try {
        const result = await apiService.getStudentProfile(user.id);
        if (result.data && result.data.backgroundData) {
          // Merge existing data with user info, prioritizing existing data
          setFormData({ ...initialData, ...result.data.backgroundData });
        } else {
          // Use initial data if no existing profile
          setFormData(initialData);
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        // Use initial data on error
        setFormData(initialData);
      }
    };

    loadExistingProfile();
  }, [user]);

  const handleInputChange = (questionId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleGroupInputChange = (groupId: string, subQuestionId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        [subQuestionId]: value
      }
    }));
  };

  const shouldShowQuestion = (question: Question): boolean => {
    if (!question.conditional) return true;
    
    const conditionalValue = formData[question.conditional.field];
    return conditionalValue === question.conditional.value;
  };

  const renderQuestion = (question: Question) => {
    if (!shouldShowQuestion(question)) return null;

    const value = formData[question.id] || '';

    switch (question.type) {
      case 'text':
      case 'email':
        const isReadonly = question.id === 'name' || question.id === 'email';
        return (
          <input
            type={question.type}
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            readOnly={isReadonly}
            className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
              isReadonly ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''
            }`}
            placeholder={question.text}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleInputChange(question.id, parseInt(e.target.value) || 0)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            placeholder="Enter number"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            rows={3}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            placeholder={question.text}
          />
        );

      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            <option value="">Select an option</option>
            {question.options?.map((option) => (
              <option key={typeof option === 'string' ? option : option.value} value={typeof option === 'string' ? option : option.value}>
                {typeof option === 'string' ? option : option.label}
              </option>
            ))}
          </select>
        );

      case 'course_dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            <option value="">Select a course</option>
            {enrolledClasses.map((enrollment) => (
              <option key={enrollment.class.id} value={enrollment.class.id}>
                {enrollment.class.name} ({enrollment.class.code})
              </option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {question.options?.map((option) => {
              const optionValue = typeof option === 'string' ? option : option.value;
              const optionLabel = typeof option === 'string' ? option : option.label;
              const isChecked = String(value) === String(optionValue);
              return (
                <label key={optionValue} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name={question.id}
                    value={optionValue}
                    checked={isChecked}
                    onChange={(e) => {
                      const newValue = typeof option === 'string' ? e.target.value : Number(e.target.value);
                      handleInputChange(question.id, newValue);
                    }}
                    className="w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                  />
                  <span className="text-gray-700">{optionLabel}</span>
                </label>
              );
            })}
          </div>
        );

      case 'checkbox':
        const checkboxValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {question.options?.map((option) => {
              const optionValue = typeof option === 'string' ? option : option.value;
              const optionLabel = typeof option === 'string' ? option : option.label;
              return (
                <label key={optionValue} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checkboxValues.includes(optionValue)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleInputChange(question.id, [...checkboxValues, optionValue]);
                      } else {
                        handleInputChange(question.id, checkboxValues.filter((v: any) => v !== optionValue));
                      }
                    }}
                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-gray-700">{optionLabel}</span>
                </label>
              );
            })}
          </div>
        );

      case 'group':
        return (
          <div className="space-y-4">
            {question.subQuestions?.map((subQuestion) => (
              <div key={subQuestion.id} className="border rounded-lg p-4 bg-gray-50">
                <p className="text-gray-800 mb-3 font-medium">{subQuestion.text}</p>
                <div className="space-y-2">
                  {subQuestion.options?.map((option) => {
                    const optionValue = typeof option === 'string' ? option : option.value;
                    const optionLabel = typeof option === 'string' ? option : option.label;
                    const groupValue = formData[question.id] || {};
                    const isChecked = String(groupValue[subQuestion.id]) === String(optionValue);
                    return (
                      <label key={optionValue} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`${question.id}_${subQuestion.id}`}
                          value={optionValue}
                          checked={isChecked}
                          onChange={(e) => {
                            const newValue = typeof option === 'string' ? e.target.value : Number(e.target.value);
                            handleGroupInputChange(question.id, subQuestion.id, newValue);
                          }}
                          className="w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                        />
                        <span className="text-gray-700">{optionLabel}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const handleNext = () => {
    if (currentSectionIndex < backgroundQuestions.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Save background data to student profile
      await apiService.updateStudentProfile(user.id, {
        backgroundData: formData
      });
      
      onSave();
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isLastSection = currentSectionIndex === backgroundQuestions.length - 1;
  const progress = ((currentSectionIndex + 1) / backgroundQuestions.length) * 100;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-lime-500 p-6 text-white">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Update Your Profile</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
            <div
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-sm mt-2 opacity-90">
            Section {currentSectionIndex + 1} of {backgroundQuestions.length}: {currentSection.title}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <motion.div
            key={currentSectionIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-6">{currentSection.title}</h3>
            
            <div className="space-y-6">
              {currentSection.questions.map((question) => (
                <div key={question.id} className={shouldShowQuestion(question) ? 'block' : 'hidden'}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {question.text}
                  </label>
                  {renderQuestion(question)}
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-6 flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentSectionIndex === 0}
            className={`px-6 py-2 rounded-lg transition-colors ${
              currentSectionIndex === 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Previous
          </button>
          
          <div className="flex space-x-3">
            {isLastSection ? (
              <button
                onClick={handleSave}
                disabled={loading}
                className={`px-6 py-2 rounded-lg transition-colors ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                } text-white`}
              >
                {loading ? 'Saving...' : 'Save Profile'}
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProfileUpdate;